import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditBasic } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { filterCourseDateDefault } from "cc-frontend/utils/filter-dates/course"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import { followContext, followedCourseEvent } from "./COURSE_FOLLOW_COURSE"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  course: Course
  planbookId: string
  isNonInstructional: boolean
}

export default function setCourseNonInstructionalAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_SET_NON_INSTRUCTIONAL",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches,
    narrative: partial(narrative, payload),
  }
}

export function patches(payload: Payload): Array<PatchModel> {
  const coursePatches = [
    Patch.create("course", payload.course.id).set(
      "attributes.isNonInstructional",
      payload.isNonInstructional,
    ).patch,
  ]
  if (!payload.isNonInstructional) return coursePatches

  // Class settings only offers "Stop Following" on lesson-plan classes, so a "Just notes" class
  // that kept its link could never drop it.
  if (payload.course.attributes.followedCourse) {
    coursePatches.push(
      Patch.create("course", payload.course.id).set("attributes.followedCourse", null).patch,
    )
  }

  for (const courseDate of filterCourseDateDefault(payload.course)) {
    if (courseDate.attributes.cardStackId === null) continue
    coursePatches.push(
      Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: courseDate.id,
      })
        .set("embedded", "attributes.cardStackId", null)
        .inc("attributes.calendar.version", 1).patch,
    )
  }
  return coursePatches
}

export const undoPatches = null

export function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)
  let courseTitle = models.course?.attributes.title
  let kind = payload.isNonInstructional ? "non-instructional" : "instructional"
  let newEvent: CourseEditBasic = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "is_non_instructional",
    primary_object_id_and_ancestor_ids: null,

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: null,
  }

  const clearedFollowedCourseInfo = payload.isNonInstructional
    ? payload.course.attributes.followedCourse
    : null
  const unfollowEvents = clearedFollowedCourseInfo
    ? [
        followedCourseEvent(findFn, userId, "removing", {
          planbookId: payload.planbookId,
          courseId: payload.course.id,
          followedCourseId: clearedFollowedCourseInfo.courseId,
        }),
      ]
    : []

  return {
    title: `Made course ${kind}`,
    titleForAnalytics: "Course - Changed Instructional",
    description: `Made ${courseTitle} ${kind}`,
    context: {
      ...defaultCourseContext(payload, models),
      ...(clearedFollowedCourseInfo
        ? followContext({
            followingCourseId: payload.course.id,
            followedCourseId: clearedFollowedCourseInfo.courseId,
          })
        : {}),
    },
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [newEvent, ...unfollowEvents],
  }
}
